/**
 * @fileoverview A mission label that turns into an input on click.
 *
 * Renaming an area is the most frequent edit on a letterbox plan (moving
 * a polygon from one van group to another), so it happens in place: in the
 * table's Label cell and in the map popup. Enter saves, Escape or leaving
 * the field cancels.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { PREDEFINED_COLORS } from 'constants.js'

// The first six are the ones ops reach for; the rest stay a row below.
const QUICK_COLOURS = PREDEFINED_COLORS.slice(0, 6)
const MORE_COLOURS = PREDEFINED_COLORS.slice(6)
const DEFAULT_COLOUR = '#7E8FA5'

const Wrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;

  .text {
    cursor: text;
    border-bottom: 1px dashed transparent;
  }
  .text:hover {
    border-bottom-color: #1878cf;
  }
  .pencil {
    background: none;
    border: 0;
    padding: 0;
    color: #1878cf;
    cursor: pointer;
    font-size: 12px;
    opacity: 0.7;
  }
  .pencil:hover {
    opacity: 1;
  }
  .swatch {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    border-radius: 3px;
    border: 1px solid rgba(0, 0, 0, 0.25);
    padding: 0;
    cursor: pointer;
  }
  .palette {
    position: absolute;
    z-index: 20;
    margin-top: 4px;
    padding: 8px;
    max-width: 196px;
    background: #ffffff;
    border: 1px solid #d9dee6;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .palette button {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.25);
    padding: 0;
    cursor: pointer;
  }
  .palette button.current {
    outline: 2px solid #1878cf;
    outline-offset: 1px;
  }
  input {
    font: inherit;
    color: inherit;
    background: transparent;
    border: 0;
    border-bottom: 1px solid #1878cf;
    outline: none;
    min-width: 200px;
  }
  .saving {
    opacity: 0.6;
  }
`

const InlineLabel = ({ mission, onRename, onRecolour, className }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(mission.label || '')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef(null)
  // Escape must win over the blur it causes (the map popup closes on
  // Escape too, which blurs the input): the flag makes that blur a cancel.
  const cancelledRef = useRef(false)
  const [palette, setPalette] = useState(false)
  const colour = mission.boundaryColour || DEFAULT_COLOUR

  const pick = useCallback(
    async value => {
      setPalette(false)
      if (value !== mission.boundaryColour) await onRecolour(mission, value)
    },
    [mission, onRecolour],
  )

  const swatch = onRecolour ? (
    <button
      type="button"
      className="swatch"
      style={{ background: colour }}
      title="Change the colour of this area"
      onClick={event => {
        event.stopPropagation()
        setPalette(open => !open)
      }}
      aria-label="Area colour"
    />
  ) : null

  const palettePopup =
    onRecolour && palette ? (
      <span className="palette" onClick={event => event.stopPropagation()}>
        {[...QUICK_COLOURS, ...MORE_COLOURS].map(option => (
          <button
            key={option.value}
            type="button"
            className={option.value === colour ? 'current' : ''}
            style={{ background: option.value }}
            title={option.label}
            onClick={() => pick(option.value)}
          />
        ))}
      </span>
    ) : null

  useEffect(() => {
    if (!editing) setDraft(mission.label || '')
  }, [mission.label, editing])

  useEffect(() => {
    if (editing) {
      cancelledRef.current = false
      if (inputRef.current) inputRef.current.select()
    }
  }, [editing])

  const cancel = useCallback(() => {
    cancelledRef.current = true
    setEditing(false)
    setDraft(mission.label || '')
  }, [mission.label])

  const save = useCallback(async () => {
    const next = draft.trim()
    if (!next || next === (mission.label || '')) return cancel()
    setSaving(true)
    try {
      await onRename(mission, next)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }, [draft, mission, onRename, cancel])

  if (!onRename)
    return (
      <Wrapper className={className}>
        {swatch}
        <span>{mission.label || mission.id}</span>
        {palettePopup}
      </Wrapper>
    )

  if (editing)
    return (
      <Wrapper className={className}>
        {swatch}
        <input
          ref={inputRef}
          value={draft}
          disabled={saving}
          className={saving ? 'saving' : ''}
          onChange={event => setDraft(event.target.value)}
          onClick={event => event.stopPropagation()}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault()
              save()
            } else if (event.key === 'Escape') {
              event.preventDefault()
              event.stopPropagation()
              cancel()
            }
          }}
          onBlur={() => {
            if (!saving && !cancelledRef.current) save()
          }}
          aria-label="Mission label"
        />
        {palettePopup}
      </Wrapper>
    )

  return (
    <Wrapper className={className}>
      {swatch}
      <span
        className="text"
        title="Click to rename"
        onClick={event => {
          event.stopPropagation()
          setEditing(true)
        }}
      >
        {mission.label || mission.id}
      </span>
      <button
        type="button"
        className="pencil"
        title="Rename"
        onClick={event => {
          event.stopPropagation()
          setEditing(true)
        }}
      >
        &#9998;
      </button>
      {palettePopup}
    </Wrapper>
  )
}

InlineLabel.propTypes = {
  mission: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    label: PropTypes.string,
    boundaryColour: PropTypes.string,
  }).isRequired,
  onRename: PropTypes.func,
  onRecolour: PropTypes.func,
  className: PropTypes.string,
}

InlineLabel.defaultProps = {
  onRename: null,
  onRecolour: null,
  className: undefined,
}

export default InlineLabel
